// Funciones de utilidad para el módulo de Despachos

/**
 * Formatea KG a toneladas con formato chileno (coma decimal, 2 decimales)
 * Ej: 234500 → "234,50"   |   50000 → "50,00"
 */
export const formatTons = (kg) => {
  if (kg === null || kg === undefined || kg === '') return '—';
  const tons = kg / 1000;
  return tons.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Convierte toneladas ingresadas por el usuario a KG para almacenamiento backend
 */
export const tonsToKg = (tons) => {
  const n = parseFloat(String(tons).replace(',', '.'));
  return isNaN(n) ? 0 : n * 1000;
};

/**
 * Convierte KG a toneladas para mostrar en inputs de formularios
 */
export const kgToTons = (kg) => {
  if (!kg && kg !== 0) return '';
  return parseFloat((kg / 1000).toFixed(3));
};


/**
 * Convierte fecha de YYYY-MM-DD a DD-MM-AAAA
 * @param {string} isoDate - Fecha en formato YYYY-MM-DD
 * @returns {string} - Fecha en formato DD-MM-AAAA
 */
export const formatDateToDDMMAAAA = (isoDate) => {
  if (!isoDate) return '';
  // Si ya está en formato DD-MM-AAAA, devolverla tal cual
  if (/^\d{2}-\d{2}-\d{4}$/.test(isoDate)) return isoDate;
  // Si está en formato YYYY-MM-DD, convertir
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    const [year, month, day] = isoDate.split('-');
    return `${day}-${month}-${year}`;
  }
  return isoDate;
};

/**
 * Convierte fecha de DD-MM-AAAA a YYYY-MM-DD (para inputs tipo date)
 * @param {string} ddmmaaaa - Fecha en formato DD-MM-AAAA
 * @returns {string} - Fecha en formato YYYY-MM-DD
 */
export const formatDateToYYYYMMDD = (ddmmaaaa) => {
  if (!ddmmaaaa) return '';
  // Si ya está en formato YYYY-MM-DD, devolverla tal cual
  if (/^\d{4}-\d{2}-\d{2}$/.test(ddmmaaaa)) return ddmmaaaa;
  // Si está en formato DD-MM-AAAA, convertir
  if (/^\d{2}-\d{2}-\d{4}$/.test(ddmmaaaa)) {
    const [day, month, year] = ddmmaaaa.split('-');
    return `${year}-${month}-${day}`;
  }
  return ddmmaaaa;
};

/**
 * Obtiene el color para la diferencia de KG
 * @param {number} kgLoaded - KG cargados
 * @param {number} kgUnloaded - KG descargados
 * @returns {string} - Clase CSS de color
 */
export const getKgDiffColor = (kgLoaded, kgUnloaded) => {
  if (!kgLoaded || !kgUnloaded) return 'text-slate-500';
  const diff = kgLoaded - kgUnloaded;
  if (diff <= 0) return 'text-emerald-600'; // Verde: 0 o ganancia
  if (diff > 100) return 'text-red-600'; // Rojo: pérdida > 100 KG
  return 'text-orange-500'; // Naranja: pérdida 0-100 KG
};

/**
 * Verifica si un camión tiene campos vacíos que se pueden agregar
 */
export const truckHasEmptyFields = (truck) => {
  return !truck.load_date || !truck.delivery_date || !truck.broker || !truck.freight_value || 
         !truck.kg_loaded || !truck.destination || !truck.commune || !truck.sale_price || 
         !truck.guide_number || !truck.kg_unloaded || !truck.sale_invoice || !truck.transport_invoice;
};

/**
 * Verifica si un camión tiene datos que se pueden modificar
 */
export const truckHasData = (truck) => {
  return truck.company_name || truck.license_plate || truck.driver_name || 
         truck.kg_loaded || truck.destination || truck.commune || 
         truck.sale_price || truck.guide_number || truck.kg_unloaded;
};

/**
 * Calcula totales de KG para un despacho
 */
export const calculateTotals = (trucks) => {
  const totalKgLoaded = trucks.reduce((sum, t) => sum + (t.kg_loaded || 0), 0);
  const totalKgUnloaded = trucks.reduce((sum, t) => sum + (t.kg_unloaded || 0), 0);
  const totalDiff = totalKgLoaded - totalKgUnloaded;
  return { totalKgLoaded, totalKgUnloaded, totalDiff };
};

/**
 * Obtiene el color para la diferencia total de KG
 */
export const getTotalDiffColor = (totalKgLoaded, totalKgUnloaded, totalDiff) => {
  if (totalKgLoaded === 0 || totalKgUnloaded === 0) return 'text-slate-500';
  if (totalDiff <= 0) return 'text-emerald-600';
  if (totalDiff > 100) return 'text-red-600';
  return 'text-orange-500';
};

/**
 * Prepara datos del camión para enviar al API
 */
export const prepareTruckDataForApi = (truckData) => ({
  order: truckData.order || null,
  company_id: truckData.company_id || null,
  truck_id: truckData.truck_id || null,
  trailer_id: truckData.trailer_id || null,
  driver_id: truckData.driver_id || null,
  load_date: truckData.load_date || null,
  delivery_date: truckData.delivery_date || null,
  broker: truckData.broker?.toUpperCase() || null,
  freight_value: truckData.freight_value || null,
  kg_loaded: truckData.kg_loaded || null,
  kg_unloaded: truckData.kg_unloaded || null,
  destination: truckData.destination?.toUpperCase() || null,
  commune: truckData.commune?.toUpperCase() || null,
  sale_price: truckData.sale_price || null,
  sale_invoice: truckData.sale_invoice?.toUpperCase() || null,
  guide_number: truckData.guide_number?.toUpperCase() || null,
  transport_invoice: truckData.transport_invoice?.toUpperCase() || null,
  negocio_id: truckData.negocio_id || null,
  compra_id: truckData.compra_id || null
});

/**
 * Inicializa datos del camión desde el camión seleccionado
 */
export const initTruckDataFromTruck = (truck) => ({
  order: truck.order || 1,
  company_id: truck.company_id || '',
  truck_id: truck.truck_id || '',
  trailer_id: truck.trailer_id || '',
  driver_id: truck.driver_id || '',
  load_date: truck.load_date || '',
  delivery_date: truck.delivery_date || '',
  broker: truck.broker || '',
  freight_value: truck.freight_value || 0,
  kg_loaded: truck.kg_loaded || 0,
  kg_unloaded: truck.kg_unloaded || 0,
  destination: truck.destination || '',
  commune: truck.commune || '',
  sale_price: truck.sale_price || 0,
  sale_invoice: truck.sale_invoice || '',
  guide_number: truck.guide_number || '',
  transport_invoice: truck.transport_invoice || '',
  negocio_id: truck.negocio_id || '',
  compra_id: truck.compra_id || ''
});
